import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { FILE_ONBOARDING_TYPE_MAPPER, ONBOARDING_STATUSES, ONBOARDING_TYPE } from 'src/constants';
import { ENDPOINT, HTTP_STATUS_RESPONSE_KEY } from 'src/constants/api';
import { ResponseDTO } from 'src/dto/base.dto';
import { DeleteFileDTO, ResubmitRequestDTO } from 'src/dto/onboarding.dto';
import {
	IAccountDetail,
	IDocument,
	IFunderSupportingDocument,
	IOnboardingRequestData
} from 'src/interfaces/accounts/account';
import { IHttpService } from 'src/services/http.service';

export interface IBaseOnboardingStore {
	accountDetail?: IAccountDetail;
	supportingDocuments?: IFunderSupportingDocument;
	saveAccountDetail(body: IAccountDetail): Promise<boolean>;
	saveDraft(): Promise<boolean>;
	submit(): Promise<boolean>;
	loadProgress(): Promise<boolean>;
	uploadFile(form: FormData): Promise<boolean>;
	uploadPlatform(form: FormData): Promise<boolean>;
	downloadFile(type: keyof IFunderSupportingDocument, id?: string): Promise<void>;
	downloadDocument(documentId: string, documentName: string): Promise<void>;
	deleteFile(id?: string): Promise<boolean>;
	updateTitle(title: string): void;
	get isFunderPendingForApproval(): boolean;
	get isBorrowerPendingForApproval(): boolean;
	get hasDetailRequested(): boolean;
	funderProgressLoaded: boolean;
	borrowerProgressLoaded: boolean;
	get getTotalFilesSize(): number;
}
export interface IOnboardingStore extends IBaseOnboardingStore {
	supportingDocuments?: IFunderSupportingDocument;
	funderOnboardingRequest?: IOnboardingRequestData;
	borrowerOnboardingRequest?: IOnboardingRequestData;
	onboardingType: ONBOARDING_TYPE;

	setFunderOnboardingRequest(data?: IOnboardingRequestData): void;
	setBorrowerOnboardingRequest(data?: IOnboardingRequestData): void;
	setOnboardingType(type: ONBOARDING_TYPE): void;
	setFunderProgressLoaded(): void;
	setBorrowerProgressLoaded(): void;
	setFunderOnboardingStatus(status: ONBOARDING_STATUSES): void;

	resubmitRequest(onboardingRequestId: string): Promise<boolean>;
}

export class OnboardingStore implements IOnboardingStore {
	accountDetail?: IAccountDetail;
	funderOnboardingRequest?: IOnboardingRequestData;
	borrowerOnboardingRequest?: IOnboardingRequestData;
	onboardingType: ONBOARDING_TYPE = ONBOARDING_TYPE.FUNDER;
	supportingDocuments?: IFunderSupportingDocument;
	funderProgressLoaded = false;
	borrowerProgressLoaded = false;
	constructor(protected readonly http: IHttpService) {
		makeObservable(this, {
			supportingDocuments: observable.deep,
			accountDetail: observable,
			funderOnboardingRequest: observable.deep,
			borrowerOnboardingRequest: observable.deep,
			funderProgressLoaded: observable,
			borrowerProgressLoaded: observable,
			setFunderOnboardingRequest: action,
			setBorrowerOnboardingRequest: action,
			setOnboardingType: action,
			loadProgress: action.bound,
			isFunderPendingForApproval: computed,
			isBorrowerPendingForApproval: computed,
			setFunderProgressLoaded: action,
			setBorrowerProgressLoaded: action,
			updateTitle: action.bound,
			setFunderOnboardingStatus: action.bound,
			getTotalFilesSize: computed,
			hasDetailRequested: computed
		});
	}

	setFunderProgressLoaded(): void {
		this.funderProgressLoaded = true;
	}

	setBorrowerProgressLoaded(): void {
		this.borrowerProgressLoaded = true;
	}

	setOnboardingType(type: ONBOARDING_TYPE): void {
		this.onboardingType = type;
	}

	setFunderOnboardingStatus(status: ONBOARDING_STATUSES): void {
		this.funderOnboardingRequest = {
			...this.funderOnboardingRequest,
			status
		};
	}

	public updateTitle(title: string): void {
		this.accountDetail = {
			...this.accountDetail,
			title
		};
	}

	public setFunderOnboardingRequest(data: IOnboardingRequestData): void {
		this.funderOnboardingRequest = data;
	}

	public setBorrowerOnboardingRequest(data: IOnboardingRequestData): void {
		this.borrowerOnboardingRequest = data;
	}

	get isFunderPendingForApproval(): boolean {
		if (!this.funderOnboardingRequest || !this.funderOnboardingRequest.status) {
			return false;
		}
		return [
			ONBOARDING_STATUSES.WaitingForApproval,
			ONBOARDING_STATUSES.Approved,
			ONBOARDING_STATUSES.Rejected
		].includes(this.funderOnboardingRequest.status);
	}

	get isBorrowerPendingForApproval(): boolean {
		if (!this.borrowerOnboardingRequest || !this.borrowerOnboardingRequest.status) {
			return false;
		}
		return [
			ONBOARDING_STATUSES.WaitingForApproval,
			ONBOARDING_STATUSES.Approved,
			ONBOARDING_STATUSES.Rejected
		].includes(this.borrowerOnboardingRequest.status);
	}

	get hasDetailRequested(): boolean {
		return this.isBorrowerPendingForApproval || this.isFunderPendingForApproval;
	}

	public async downloadFile(type: keyof IFunderSupportingDocument, id?: string): Promise<void> {
		if (!id || !this.supportingDocuments || !this.supportingDocuments[type]) {
			return;
		}
		const documents: IDocument | IDocument[] | undefined = this.supportingDocuments[type];
		let doc: IDocument | undefined;

		if (!documents) {
			return;
		}
		if (Array.isArray(documents)) {
			doc = documents.find((d) => d.id == id);
		} else {
			doc = documents;
		}
		if (!doc) {
			return;
		}
		await this.http.downloadFile(
			ENDPOINT.FUNDER_FILE_DOWNLOAD.concat(id),
			{},
			{
				name: doc.fileName || ''
			}
		);
	}

	public async downloadDocument(documentId: string, documentName: string): Promise<void> {
		await this.http.downloadFile(
			ENDPOINT.FUNDER_FILE_DOWNLOAD.concat(documentId),
			{},
			{
				name: documentName
			}
		);
	}

	public async deleteFile(id?: string): Promise<boolean> {
		if (!id) {
			return true;
		}
		const dto = new DeleteFileDTO({
			documentId: id
		});

		const res: ResponseDTO<IFunderSupportingDocument> = await this.http.request<
			DeleteFileDTO,
			IFunderSupportingDocument
		>(dto);
		if (res.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.supportingDocuments = {
					...res.data
				};
			});
			return true;
		}
		return false;
	}
	async saveAccountDetail(body: IAccountDetail): Promise<boolean> {
		throw new Error('Should let the child implement this.' + body);
	}
	async uploadFile(form: FormData): Promise<boolean> {
		const obType = `${FILE_ONBOARDING_TYPE_MAPPER.get(this.onboardingType)}`;
		form.append('onboardingType', obType);
		const res: ResponseDTO<IFunderSupportingDocument> = await this.http.uploadFile(
			ENDPOINT.FUNDER_ONBOARDING_FILE_UPLOAD,
			form
		);
		if (res.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.supportingDocuments = {
					...res.data
				};
			});
			return true;
		}
		return false;
	}
	async uploadPlatform(form: FormData): Promise<boolean> {  
		const res: ResponseDTO<IFunderSupportingDocument> = await this.http.uploadFile(
			ENDPOINT.USER_ONBOARDING_FLATFORM_SUBMIT,
			form
		);
		if (res.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			return true;
		}
		return false;
	}

	async loadProgress(): Promise<boolean> {
		throw new Error('Not implement let the child...');
	}

	async saveDraft(): Promise<boolean> {
		throw new Error('Not implement let the child...');
	}

	async submit(): Promise<boolean> {
		throw new Error('Not implement let the child...');
	}

	get getTotalFilesSize(): number {
		let rs = 0;
		const documents: IFunderSupportingDocument = this.supportingDocuments || {};
		const keys = Object.keys(documents);
		for (const k of keys) {
			const doc = documents[k as keyof IFunderSupportingDocument];
			if (doc && doc.length) {
				doc.forEach((d) => {
					rs += d.size || 0;
				});
			}
		}
		return rs;
	}

	public async resubmitRequest(onboardingRequestId: string): Promise<boolean> {
		const dto = new ResubmitRequestDTO({ onboardingRequestId });

		const rs: ResponseDTO<string> = await this.http.request<ResubmitRequestDTO, string>(dto);

		if (rs.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			return true;
		}

		return false;
	}
}
